import { useCallback, useEffect, useMemo, useState } from 'react'
import { emptyBookingDraft, getBookingDraft, saveBookingDraft } from '../../data/ceramic-booking'
import {
  emptyAddOnSelection,
  normalizeAddOnSelection,
  toggleAddOn,
  type AddOnSelection,
} from '../../data/protection-addons'
import { formatCurrencyFromCents, type CeramicPackage } from '../../data/protection-catalog'
import { callSupabaseFunction } from '../../lib/api'
import {
  formatAppointmentDate,
  formatAppointmentTime,
} from '../../lib/appointment-scheduling'
import {
  clampBookStep,
  furthestBookStep,
  isAppointmentConfirmed,
  nextBookStep,
  previousBookStep,
  type BookStepId,
} from '../../lib/book-flow'
import {
  type DepositStatus,
  parseCheckoutReturnSearch,
} from '../../lib/deposit-checkout'
import { resolveSelectedProtection } from '../../lib/selected-protection'
import { useCeramicPackages } from '../../lib/use-ceramic-packages'
import {
  BookAppointmentStep,
  type AppointmentBookingView,
} from '../book/book-appointment-step'
import { BookConfirmationStep } from '../book/book-confirmation-step'
import { BookDepositStep, type DepositUiState } from '../book/book-deposit-step'
import { BookSelectionSummary } from '../book/book-selection-summary'
import { BookSteps } from '../book/book-steps'
import { PageShell } from '../layout/page-shell'
import { AddOnChoiceList } from '../protection/addon-choice-list'
import { ProtectionSelector } from '../protection/protection-selector'
import { PageTitle } from '../seo/page-title'
import { Button, ButtonLink } from '../ui/button'

export interface BookPageProps {}

function selectionFromDraft(): AddOnSelection {
  const draft = getBookingDraft()
  return {
    addOnIds: draft.addOns ?? [],
    noAdditionalProtection: (draft.addOns ?? []).length === 0,
  }
}

async function fetchBookingStatus(query: Record<string, string>) {
  const response = await callSupabaseFunction('booking-status', {}, query)
  const payload = await response.json() as {
    ok?: boolean
    depositStatus?: DepositStatus
    booking?: AppointmentBookingView
  }
  return payload
}

export function BookPage(_: Readonly<BookPageProps>) {
  const packages = useCeramicPackages()
  const [step, setStep] = useState<BookStepId>('protection')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [addOnSelection, setAddOnSelection] = useState<AddOnSelection>(emptyAddOnSelection)
  const [addOnsCommitted, setAddOnsCommitted] = useState(false)
  const [depositStatus, setDepositStatus] = useState<DepositStatus>('not_started')
  const [depositUi, setDepositUi] = useState<DepositUiState>('idle')
  const [depositMessage, setDepositMessage] = useState<string | null>(null)
  const [stripeEnabled, setStripeEnabled] = useState(false)
  const [stripeMode, setStripeMode] = useState<'test' | 'live' | 'unconfigured' | 'unknown'>('unconfigured')
  const [appointmentBooking, setAppointmentBooking] = useState<AppointmentBookingView | null>(null)
  const [confirmationEmailStatus, setConfirmationEmailStatus] = useState<string | null>(null)
  const [resumeReference, setResumeReference] = useState<string | null>(null)
  const [reservationExpired, setReservationExpired] = useState(false)

  const checkoutReturn = parseCheckoutReturnSearch(
    typeof window === 'undefined' ? '' : window.location.search,
  ).checkout

  const paymentPaid = depositStatus === 'paid'
  const appointmentConfirmed = isAppointmentConfirmed(appointmentBooking?.appointmentStatus)
  const appointmentReserved = Boolean(
    appointmentBooking?.reservationActive
      && appointmentBooking.appointmentStatus === 'reserved'
      && !reservationExpired,
  )
  const machineState = {
    protectionId: selectedId,
    addOnsCommitted,
    appointmentReserved,
    reservationExpired,
    paymentPaid,
    appointmentConfirmed,
    checkout: checkoutReturn,
  }
  const visibleStep = clampBookStep(step, machineState)

  const handleReservationExpired = useCallback(() => {
    setReservationExpired(true)
    setDepositUi('idle')
    setStep('appointment')
  }, [])

  useEffect(() => {
    let cancelled = false

    async function hydrate() {
      const resolved = resolveSelectedProtection({
        search: window.location.search,
        packages,
      })
      const draft = getBookingDraft()
      const returned = parseCheckoutReturnSearch(window.location.search)
      const protectionId = resolved?.id ?? draft.packageId ?? null

      if (resolved) setSelectedId(resolved.id)
      else if (draft.packageId) setSelectedId(draft.packageId)
      setAddOnSelection(selectionFromDraft())
      setAddOnsCommitted(Boolean(draft.addOnsCommitted))

      async function applyPaidBooking(booking?: AppointmentBookingView) {
        if (booking?.publicReference) {
          saveBookingDraft({ ...getBookingDraft(), publicReference: booking.publicReference })
        }
        setDepositStatus('paid')
        setDepositUi('paid')
        if (booking) setAppointmentBooking(booking)
        const confirmed = isAppointmentConfirmed(booking?.appointmentStatus)
        setResumeReference(booking?.publicReference ?? draft.publicReference ?? null)
        setReservationExpired(Boolean(booking?.reservationExpired))
        setStep(furthestBookStep({
          protectionId,
          addOnsCommitted: true,
          appointmentReserved: Boolean(booking?.reservationActive && booking.appointmentStatus === 'reserved'),
          reservationExpired: Boolean(booking?.reservationExpired),
          paymentPaid: true,
          appointmentConfirmed: confirmed,
        }))
      }

      if (returned.checkout === 'cancelled') {
        let reserved = false
        let expired = false
        if (draft.publicReference) {
          try {
            const payload = await fetchBookingStatus({ reference: draft.publicReference })
            if (payload.booking) {
              setAppointmentBooking(payload.booking)
              reserved = Boolean(payload.booking.reservationActive && payload.booking.appointmentStatus === 'reserved')
              expired = Boolean(payload.booking.reservationExpired)
              setReservationExpired(expired)
            }
          } catch {
            // Local selections still resume if status lookup fails.
          }
        }
        setStep(furthestBookStep({
          protectionId,
          addOnsCommitted: true,
          appointmentReserved: reserved && !expired,
          reservationExpired: expired,
          paymentPaid: false,
          appointmentConfirmed: false,
          checkout: 'cancelled',
        }))
        setDepositUi(expired ? 'idle' : 'cancelled')
        setDepositStatus('cancelled')
        if (expired) {
          setDepositMessage('Your appointment hold has expired. Please choose a new appointment time before paying.')
        }
        return
      }

      if (returned.checkout === 'return') {
        setStep('deposit')
        if (!returned.sessionId) {
          setDepositUi('error')
          setDepositMessage('Payment could not be verified from this return URL.')
          return
        }
        setDepositUi('verifying')
        for (let attempt = 0; attempt < 8; attempt += 1) {
          if (cancelled) return
          try {
            const payload = await fetchBookingStatus({ session_id: returned.sessionId })
            if (payload.ok && payload.depositStatus === 'paid') {
              await applyPaidBooking(payload.booking)
              return
            }
          } catch {
            // Retry while the webhook or session retrieval settles.
          }
          await new Promise((resolve) => window.setTimeout(resolve, 750))
        }
        if (!cancelled) {
          setDepositUi('error')
          setDepositMessage('We could not confirm the deposit yet. Try refresh, or pay again if needed.')
        }
        return
      }

      if (draft.publicReference) {
        try {
          const payload = await fetchBookingStatus({ reference: draft.publicReference })
          if (cancelled) return
          if (payload.ok && payload.depositStatus) {
            setDepositStatus(payload.depositStatus)
            if (payload.booking) setAppointmentBooking(payload.booking)
            const expired = Boolean(payload.booking?.reservationExpired)
            const reserved = Boolean(
              payload.booking?.reservationActive && payload.booking.appointmentStatus === 'reserved',
            )
            setReservationExpired(expired)
            if (payload.depositStatus === 'paid') {
              setDepositUi('paid')
              await applyPaidBooking(payload.booking)
              return
            }
            setStep(furthestBookStep({
              protectionId,
              addOnsCommitted: Boolean(draft.addOnsCommitted),
              appointmentReserved: reserved && !expired,
              reservationExpired: expired,
              paymentPaid: false,
              appointmentConfirmed: false,
            }))
            return
          }
        } catch {
          // Keep the local funnel usable if status lookup is unavailable.
        }
      }

      if (!cancelled) {
        setStep(furthestBookStep({
          protectionId,
          addOnsCommitted: Boolean(draft.addOnsCommitted),
          appointmentReserved: false,
          reservationExpired: false,
          paymentPaid: false,
          appointmentConfirmed: false,
        }))
      }
    }

    void hydrate()
    return () => {
      cancelled = true
    }
    // Hydrate from the current URL and draft once on mount. Package catalogue
    // refreshes must not change which booking step the customer is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    callSupabaseFunction('runtime-config')
      .then((response) => response.json())
      .then((payload: {
        featureFlags?: { stripeDepositsEnabled?: boolean; stripeMode?: 'test' | 'live' | 'unconfigured' | 'unknown' }
      }) => {
        setStripeEnabled(payload.featureFlags?.stripeDepositsEnabled === true)
        setStripeMode(payload.featureFlags?.stripeMode ?? 'unconfigured')
      })
      .catch(() => {
        setStripeEnabled(false)
        setStripeMode('unconfigured')
      })
  }, [])

  const selectedPackage = packages.find((item) => item.id === selectedId) ?? null
  const publicReference = getBookingDraft().publicReference ?? appointmentBooking?.publicReference ?? ''
  const resumingPaidBooking = paymentPaid && Boolean(resumeReference)

  function persistDraft(
    nextPackage: CeramicPackage | null,
    nextAddOns: AddOnSelection,
    extras: { addOnsCommitted?: boolean } = {},
  ) {
    const normalized = normalizeAddOnSelection(nextAddOns)
    saveBookingDraft({
      ...getBookingDraft(),
      servicePath: 'ceramic',
      packageId: nextPackage?.id,
      startingPriceCents: nextPackage?.startingPriceCents,
      addOns: normalized.addOnIds,
      noAdditionalProtection: normalized.noAdditionalProtection,
      addOnsCommitted: extras.addOnsCommitted ?? getBookingDraft().addOnsCommitted,
    })
  }

  function persistAddOns(next: AddOnSelection) {
    const normalized = {
      addOnIds: next.addOnIds,
      noAdditionalProtection: false,
    }
    setAddOnSelection(normalized)
    persistDraft(selectedPackage, normalized)
  }

  function startFreshDraft(item: CeramicPackage) {
    saveBookingDraft({
      ...emptyBookingDraft,
      servicePath: 'ceramic',
      packageId: item.id,
      startingPriceCents: item.startingPriceCents,
      addOns: [],
      addOnsCommitted: false,
      noAdditionalProtection: false,
    })
    setAddOnSelection(emptyAddOnSelection())
    setAddOnsCommitted(false)
    setDepositStatus('not_started')
    setDepositUi('idle')
    setAppointmentBooking(null)
    setResumeReference(null)
    setReservationExpired(false)
    setDepositMessage(null)
  }

  function goNext() {
    if (visibleStep === 'protection' && !selectedId) return
    if (visibleStep === 'addons') {
      const normalized = normalizeAddOnSelection(addOnSelection)
      setAddOnSelection(normalized)
      setAddOnsCommitted(true)
      persistDraft(selectedPackage, normalized, { addOnsCommitted: true })
      setStep('appointment')
      return
    }
    if (visibleStep === 'deposit' && !paymentPaid) return
    const next = nextBookStep(visibleStep)
    if (next) setStep(next)
  }

  function goBack() {
    const previous = previousBookStep(visibleStep)
    if (previous) setStep(previous)
  }

  const resumeNotice = useMemo(() => {
    if (!resumingPaidBooking || !resumeReference) return null
    if (visibleStep !== 'appointment' && visibleStep !== 'confirmation') return null
    return `Resuming booking ${resumeReference}. The $100 deposit for this booking is already paid.`
  }, [resumingPaidBooking, resumeReference, visibleStep])

  return (
    <PageShell className="booking-page">
      <PageTitle title="Book Ceramic Protection | Autoglow Detailing" />
      <section className="book-flow section-container">
        <div className="booking-heading left">
          <p className="eyebrow">Book with Autoglow</p>
          <h1>Ceramic protection booking</h1>
          <p>
            Choose protection if you have not already, complete optional add-ons, choose your appointment,
            then pay the $100 booking deposit.
          </p>
        </div>
        <BookSteps activeStep={visibleStep} />
        {resumeNotice ? (
          <p className="selected-protection-note" role="status">{resumeNotice}</p>
        ) : null}
        <div className="book-flow-layout">
          <div>
            {visibleStep === 'protection' ? (
              <section aria-labelledby="book-protection-heading" className="book-step-panel">
                <h2 id="book-protection-heading">Step 1 — Protection</h2>
                {selectedPackage ? (
                  <p className="selected-protection-note" role="status">
                    {selectedPackage.displayName} selected · {selectedPackage.productName} · From{' '}
                    {formatCurrencyFromCents(selectedPackage.startingPriceCents)} inc. GST
                  </p>
                ) : (
                  <p>Select 1, 3 or 5 Year protection to continue.</p>
                )}
                <ProtectionSelector
                  onSelect={(item) => {
                    if (paymentPaid) startFreshDraft(item)
                    else persistDraft(item, addOnSelection, { addOnsCommitted: false })
                    setSelectedId(item.id)
                    setAddOnsCommitted(false)
                    setStep('addons')
                  }}
                  packages={packages}
                  showContinueActions={false}
                  variant="full"
                />
              </section>
            ) : null}

            {visibleStep === 'addons' ? (
              <section aria-labelledby="book-addons-heading" className="book-step-panel">
                <h2 id="book-addons-heading">Complete Your Protection</h2>
                <p>
                  Further enhance your ceramic coating with additional GYEON protection for the areas
                  of your vehicle that experience the greatest exposure to the elements. Add-ons are
                  optional — continue with none selected if you only want ceramic coating.
                </p>
                <AddOnChoiceList
                  onToggle={(id) => persistAddOns(toggleAddOn(addOnSelection, id))}
                  selection={addOnSelection}
                />
              </section>
            ) : null}

            {visibleStep === 'deposit' ? (
              <BookDepositStep
                addOnIds={addOnSelection.addOnIds}
                depositStatus={depositStatus}
                message={depositMessage}
                noAdditionalProtection={addOnSelection.addOnIds.length === 0}
                onMessage={setDepositMessage}
                onReservationExpired={handleReservationExpired}
                onStatus={setDepositStatus}
                onUiState={setDepositUi}
                packageKey={selectedId}
                stripeEnabled={stripeEnabled}
                stripeMode={stripeMode}
                uiState={depositUi}
              />
            ) : null}

            {visibleStep === 'appointment' ? (
              <BookAppointmentStep
                addOnIds={addOnSelection.addOnIds}
                expiredNotice={reservationExpired
                  ? 'Your appointment hold has expired. Please choose a new appointment time before paying.'
                  : null}
                onConfirmed={(booking, emailStatus) => {
                  setAppointmentBooking(booking)
                  setConfirmationEmailStatus(emailStatus ?? null)
                  setReservationExpired(false)
                  setStep('confirmation')
                }}
                onReserved={(booking) => {
                  setAppointmentBooking(booking)
                  setResumeReference(booking.publicReference)
                  setReservationExpired(false)
                  setStep('deposit')
                }}
                packageKey={selectedId}
                publicReference={publicReference || null}
              />
            ) : null}

            {visibleStep === 'confirmation' ? (
              <BookConfirmationStep
                booking={appointmentBooking}
                emailStatus={confirmationEmailStatus}
              />
            ) : null}

            <div className="book-step-actions">
              {previousBookStep(visibleStep) ? (
                <Button onClick={goBack} type="button" variant="ghost">
                  Back
                </Button>
              ) : (
                <ButtonLink href="/ceramic-protection" variant="ghost">
                  Explore Ceramic Protection
                </ButtonLink>
              )}
              {visibleStep === 'deposit' && paymentPaid && appointmentConfirmed ? (
                <Button onClick={goNext} type="button">
                  Continue to Confirmation
                </Button>
              ) : null}
              {visibleStep !== 'deposit' && visibleStep !== 'appointment' && visibleStep !== 'confirmation' && nextBookStep(visibleStep) ? (
                <Button
                  disabled={visibleStep === 'protection' && !selectedId}
                  onClick={goNext}
                  type="button"
                >
                  Continue
                </Button>
              ) : null}
            </div>
          </div>
          <BookSelectionSummary
            addOnSelection={addOnSelection}
            appointment={appointmentBooking?.appointmentStartAt ? {
              date: formatAppointmentDate(
                appointmentBooking.appointmentStartAt,
                appointmentBooking.appointmentTimezone || 'Australia/Melbourne',
              ),
              time: formatAppointmentTime(
                appointmentBooking.appointmentStartAt,
                appointmentBooking.appointmentTimezone || 'Australia/Melbourne',
              ),
            } : null}
            customer={appointmentBooking?.customer ?? (getBookingDraft().customer.name ? getBookingDraft().customer : null)}
            depositPaid={paymentPaid}
            selectedPackage={selectedPackage}
            vehicle={appointmentBooking?.vehicle ?? (getBookingDraft().vehicle.make ? getBookingDraft().vehicle : null)}
          />
        </div>
      </section>
    </PageShell>
  )
}

export default BookPage
